/**
 * Firebase Configuration - Unified Service Access
 * 
 * This file provides access to Firebase services. All initialization is handled
 * by firebase-loader.ts which uses SYNCHRONOUS imports to ensure shared internal state.
 * 
 * ARCHITECTURE:
 * 1. firebase-loader.ts - Handles synchronous imports and initialization
 * 2. config.ts (this file) - Provides typed access to initialized services
 * 3. auth.ts - Provides the Auth proxy for convenient Auth access
 */

import { 
  app,
  initializeFirebase, 
  waitForFirebase, 
  isFirebaseInitialized,
  getFirebaseModules,
  getAuthInstance as loaderGetAuth,
  getFirestoreInstance as loaderGetFirestore,
  getStorageInstance as loaderGetStorage,
  getFunctionsInstance as loaderGetFunctions,
  getDatabaseInstance as loaderGetDatabase,
  getAnalyticsInstance as loaderGetAnalytics,
  firebaseConfig
} from './firebase-loader';

import type { Auth } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import type { FirebaseStorage } from 'firebase/storage';
import type { Analytics } from 'firebase/analytics';
import type { Functions } from 'firebase/functions';
import type { Database } from 'firebase/database';
import type { FirebaseApp } from 'firebase/app';

// Re-export the app and initialization functions
export { app, initializeFirebase, waitForFirebase, isFirebaseInitialized, getFirebaseModules, firebaseConfig };

// ============================================================================
// ASYNC GETTERS - Guaranteed to return initialized instances
// ============================================================================

/**
 * Get Auth instance (async - waits for initialization)
 */
export async function getAuthInstanceAsync(): Promise<Auth> {
  await waitForFirebase();
  const auth = loaderGetAuth();
  if (!auth) {
    throw new Error('Firebase Auth not available. Ensure initializeFirebase() was called.');
  }
  return auth;
}

/**
 * Get Firestore instance (async - waits for initialization)
 */
export async function getFirestoreInstanceAsync(): Promise<Firestore> {
  await waitForFirebase();
  const firestore = loaderGetFirestore();
  if (!firestore) {
    throw new Error('Firebase Firestore not available. Ensure initializeFirebase() was called.');
  }
  return firestore;
}

/**
 * Get Storage instance (async - waits for initialization)
 */
export async function getStorageInstanceAsync(): Promise<FirebaseStorage> {
  await waitForFirebase();
  const storage = loaderGetStorage();
  if (!storage) {
    throw new Error('Firebase Storage not available. Ensure initializeFirebase() was called.');
  }
  return storage;
}

/**
 * Get Functions instance (async - waits for initialization)
 */
export async function getFunctionsInstanceAsync(): Promise<Functions> {
  await waitForFirebase();
  const functions = loaderGetFunctions();
  if (!functions) {
    throw new Error('Firebase Functions not available. Ensure initializeFirebase() was called.');
  }
  return functions;
}

/**
 * Get Database instance (async - waits for initialization)
 */
export async function getDatabaseInstanceAsync(): Promise<Database> {
  await waitForFirebase();
  const database = loaderGetDatabase();
  if (!database) {
    throw new Error('Firebase Database not available. Ensure initializeFirebase() was called.');
  }
  return database;
}

/**
 * Get Analytics instance (async - may return null if not configured)
 */
export async function getAnalyticsInstanceAsync(): Promise<Analytics | null> {
  await waitForFirebase();
  return loaderGetAnalytics();
}

// ============================================================================
// SYNC GETTERS - Throw if not initialized (for use after confirmed init)
// ============================================================================

export function getAuthInstance(): Auth {
  if (!isFirebaseInitialized()) {
    throw new Error('Auth not initialized. Call getAuthInstanceAsync() or ensure initializeFirebase() completed.');
  }
  const auth = loaderGetAuth();
  if (!auth) {
    throw new Error('Auth not available.');
  }
  return auth;
}

export function getFirestoreInstance(): Firestore {
  if (!isFirebaseInitialized()) {
    throw new Error('Firestore not initialized. Use getFirestoreInstanceAsync() instead.');
  }
  const firestore = loaderGetFirestore();
  if (!firestore) {
    throw new Error('Firestore not available.');
  }
  return firestore;
}

export function getStorageInstance(): FirebaseStorage {
  if (!isFirebaseInitialized()) {
    throw new Error('Storage not initialized. Use getStorageInstanceAsync() instead.');
  }
  const storage = loaderGetStorage();
  if (!storage) {
    throw new Error('Storage not available.');
  }
  return storage;
}

export function getFunctionsInstance(): Functions {
  if (!isFirebaseInitialized()) {
    throw new Error('Functions not initialized. Use getFunctionsInstanceAsync() instead.');
  }
  const functions = loaderGetFunctions();
  if (!functions) {
    throw new Error('Functions not available.');
  }
  return functions;
}

export function getDatabaseInstance(): Database {
  if (!isFirebaseInitialized()) {
    throw new Error('Database not initialized. Use getDatabaseInstanceAsync() instead.');
  }
  const database = loaderGetDatabase();
  if (!database) {
    throw new Error('Database not available.');
  }
  return database;
}

export function getAnalyticsInstance(): Analytics | null {
  if (!isFirebaseInitialized()) {
    return null;
  }
  return loaderGetAnalytics();
}

// ============================================================================
// PROXY EXPORTS - Backward compatible
// ============================================================================

/**
 * Resilient Auth proxy
 */
export const auth = new Proxy({} as Auth, {
  get(_target, prop) {
    if (isFirebaseInitialized()) {
      const authInstance = loaderGetAuth();
      if (authInstance) {
        return (authInstance as any)[prop];
      }
    }
    
    // Special handling for currentUser - return null if not initialized
    if (prop === 'currentUser') {
      return null;
    }
    
    // Special handling for onAuthStateChanged
    if (prop === 'onAuthStateChanged') {
      return (callback: (user: any) => void) => {
        let unsubscribe: (() => void) | null = null;
        let isUnsubscribed = false;
        
        getAuthInstanceAsync().then((authInstance) => {
          if (isUnsubscribed) return;
          
          const modules = getFirebaseModules();
          if (modules.auth) {
            unsubscribe = modules.auth.onAuthStateChanged(authInstance, callback);
          } else {
            callback(null);
          }
        }).catch(err => {
          console.error('[Firebase Config] Auth not available for onAuthStateChanged:', err);
          callback(null);
        });
        
        return () => {
          isUnsubscribed = true;
          if (unsubscribe) unsubscribe();
        };
      };
    }
    
    throw new Error(`Firebase Auth not initialized. Property '${String(prop)}' accessed before initialization.`);
  }
});

export const firestore = new Proxy({} as Firestore, {
  get(_target, prop) {
    if (isFirebaseInitialized()) {
      const firestoreInstance = loaderGetFirestore();
      if (firestoreInstance) {
        const value = (firestoreInstance as any)[prop];
        if (typeof value === 'function') {
          return value.bind(firestoreInstance);
        }
        return value;
      }
    }
    
    throw new Error(`Firebase Firestore not initialized. Property '${String(prop)}' accessed before initialization.`);
  },
  getPrototypeOf(_target) {
    if (isFirebaseInitialized()) {
      const firestoreInstance = loaderGetFirestore();
      if (firestoreInstance) {
        return Object.getPrototypeOf(firestoreInstance);
      }
    }
    return Object.prototype;
  }
});

export const storage = new Proxy({} as FirebaseStorage, {
  get(_target, prop) {
    if (isFirebaseInitialized()) {
      const storageInstance = loaderGetStorage();
      if (storageInstance) {
        const value = (storageInstance as any)[prop];
        if (typeof value === 'function') {
          return value.bind(storageInstance);
        }
        return value;
      }
    }
    
    throw new Error(`Firebase Storage not initialized. Property '${String(prop)}' accessed before initialization.`);
  },
  getPrototypeOf(_target) {
    if (isFirebaseInitialized()) {
      const storageInstance = loaderGetStorage();
      if (storageInstance) {
        return Object.getPrototypeOf(storageInstance);
      }
    }
    return Object.prototype;
  }
});

export const analytics = new Proxy({} as Analytics, {
  get(_target, prop) {
    if (isFirebaseInitialized()) {
      const analyticsInstance = loaderGetAnalytics();
      if (analyticsInstance) {
        return (analyticsInstance as any)[prop];
      }
    }
    
    console.warn(`[Firebase Config] Analytics not ready. Property '${String(prop)}' may not work.`);
    return undefined;
  }
});

export const functions = new Proxy({} as Functions, {
  get(_target, prop) {
    if (isFirebaseInitialized()) {
      const functionsInstance = loaderGetFunctions();
      if (functionsInstance) {
        const value = (functionsInstance as any)[prop];
        if (typeof value === 'function') {
          return value.bind(functionsInstance);
        }
        return value;
      }
    }
    
    throw new Error(`Firebase Functions not initialized. Property '${String(prop)}' accessed before initialization.`);
  },
  getPrototypeOf(_target) {
    if (isFirebaseInitialized()) {
      const functionsInstance = loaderGetFunctions();
      if (functionsInstance) {
        return Object.getPrototypeOf(functionsInstance);
      }
    }
    return Object.prototype;
  }
});

export const database = new Proxy({} as Database, {
  get(_target, prop) {
    if (isFirebaseInitialized()) {
      const databaseInstance = loaderGetDatabase();
      if (databaseInstance) {
        const value = (databaseInstance as any)[prop];
        if (typeof value === 'function') {
          return value.bind(databaseInstance);
        }
        return value;
      }
    }
    
    throw new Error(`Firebase Database not initialized. Property '${String(prop)}' accessed before initialization.`);
  },
  getPrototypeOf(_target) {
    if (isFirebaseInitialized()) {
      const databaseInstance = loaderGetDatabase();
      if (databaseInstance) {
        return Object.getPrototypeOf(databaseInstance);
      }
    }
    return Object.prototype;
  }
});

// ============================================================================
// LEGACY COMPATIBILITY
// ============================================================================

/**
 * Initialize all services at once
 * @deprecated Use initializeFirebase() from firebase-loader.ts instead
 */
export async function initializeAllServices(): Promise<void> {
  await initializeFirebase();
  console.log('[Firebase Config] All services initialized via initializeAllServices()');
}
